import { ALLOWED_CURRENCIES, createOrganizationProduct, getPriceCentsFromProduct, updateOrganizationProduct } from '../helpers/api/products-api';
import { BallotPurchaseableDetailsLoad, ConnectedAccountStatus, CreateOrganizationProductRequest, FixedPriceStructureDetails, FormPurchaseableDetailsLoad, GetOrganizationProductResponse, LogicFieldType, PaymentTargetType, PaywallPurchaseableDetailsLoad, PriceStructure, ProductPeriod, VisibilityField } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { delay, isDefined } from '../helpers/utils';

import { FormVisibilityComponent } from './form-visibility-component';
import { ProxiSelectfield } from './theme/proxi-selectfield';
import { ProxiTextfield } from './theme/proxi-textfield';
import accountInfo from '../helpers/state/account-info';
import { getMainPage } from '../helpers/page-name';
import organizationInfo from '../helpers/state/organization-info';
import { standardFields } from '../helpers/constants';
import topicData from '../helpers/state/topic-data';

@customElement('create-product-component')
export class CreateProductComponent extends LitElement {

    @property({ type: Object })
    private existing_product?: GetOrganizationProductResponse;

    @property({ type: Object })
    private readonly stripeInfo: ConnectedAccountStatus | undefined = undefined;

    @property()
    private readonly payment_target_type?: PaymentTargetType;

    @property({ type: Object })
    private payment_target_entity_id: string | undefined;

    @state()
    private product: CreateOrganizationProductRequest | undefined;

    @state()
    private previewMode: boolean = false;

    @state()
    private reloading: boolean = false;

    @state()
    visibilityTargetOptions: VisibilityField[] = [];

    private readonly amplitude_callsite: string = 'Create Product Component';

    public async setExistingProduct(product: GetOrganizationProductResponse) {
        this.reloading = true;
        this.product = undefined;
        this.existing_product = product;
        this.product = {
            _id: product._id,
            name: product.name,
            description: product.description,
            statement_descriptor: product.statement_descriptor,
            pricing_structure: product.pricing_structure,
            currency_code: product.currency_code,
            support_email: product.support_email,
            proxi_fee_structure: product.proxi_fee_structure,
            tax_structure: product.tax_structure,
            item_details: product.item_details,
        };

        if (isDefined(this.product) && 'Form' in this.product.item_details) {
            this.getVisibilityOptions();
        }

        this.reloading = false;
    }


    private readonly defaultFormPurchaseableDetailsLoad: FormPurchaseableDetailsLoad =
    {Form: {
        topic_id: { $oid: topicData.topic?._id.$oid ?? '' },
        visibility_rules: [],
        post_submission_action: 'auto_approve'
    }};

    private readonly defaultPricingStructureDetails: FixedPriceStructureDetails = {
        Fixed: {
            amount_cents: 500
        }
    };

    private readonly defaultPaywallPurchaseableDetailsLoad: PaywallPurchaseableDetailsLoad =
    {Paywall: {
        topic_id: { $oid: topicData.topic?._id.$oid ?? '' },
        gate_id: undefined
    }};

    private readonly defaultBallotPurchaseableDetailsLoad: BallotPurchaseableDetailsLoad =
    {Ballot: {
        election_id: { $oid: '' },
        topic_id: { $oid: topicData.topic?._id.$oid ?? '' },
    }};

    private readonly defaultActivatedChallengePurchaseableDetailsLoad: any =
    {ActivatedChallenge: {
        challenge_id: { $oid: '' }
    }};

    private readonly defaultFormProductRequest: CreateOrganizationProductRequest = {
        name: 'Form Submission Fee',
        description: 'Pay-per-submission access to custom forms',
        statement_descriptor: 'SUBMISSION',
        pricing_structure: this.defaultPricingStructureDetails,
        currency_code: 'usd',
        support_email: accountInfo.account?.email ?? '',
        proxi_fee_structure: 'none',
        tax_structure: 'none',
        item_details: this.defaultFormPurchaseableDetailsLoad
    };

    // Default CreateOrganizationProductRequest for Paywall
    private readonly defaultPaywallProductRequest: CreateOrganizationProductRequest = {
        name: 'Map Access',
        description: 'One-time payment for map access',
        statement_descriptor: 'MAP_ACCESS',
        pricing_structure: this.defaultPricingStructureDetails,
        currency_code: 'usd',
        support_email: accountInfo.account?.email ?? '',
        proxi_fee_structure: 'none',
        tax_structure: 'none',
        item_details: this.defaultPaywallPurchaseableDetailsLoad
    };

    private get defaultBallotProductRequest(): CreateOrganizationProductRequest {
        return {
            name: 'Ballot Submission Fee',
            description: 'Pay-per-submission access to ballots',
            statement_descriptor: 'BALLOT_SUBMISSION',
            pricing_structure: this.defaultPricingStructureDetails,
            currency_code: 'usd',
            support_email: accountInfo.account?.email ?? '',
            proxi_fee_structure: 'none',
            tax_structure: 'none',
            item_details: { Ballot: {
                election_id: { $oid: this.payment_target_entity_id ?? '' },
                topic_id: { $oid: topicData.topic?._id.$oid ?? '' },
            } }
        };
    }

    private get defaultActivatedChallengeProductRequest(): CreateOrganizationProductRequest {
        return {
            name: 'Experience Entry Cost',
            description: 'Charge to enter this experience',
            statement_descriptor: 'EXPERIENCE',
            pricing_structure: this.defaultPricingStructureDetails,
            currency_code: 'usd',
            support_email: accountInfo.account?.email ?? '',
            proxi_fee_structure: 'none',
            tax_structure: 'none',
            item_details: this.defaultActivatedChallengePurchaseableDetailsLoad
        };
    }

    async openPreview(product: CreateOrganizationProductRequest | undefined) {
        if (!isDefined(product)) {
            console.error('openPreview called with undefined product');
            return;
        }
        this.previewMode = true;
    }

    async instantiateProduct(entity_id: string | undefined, entity_type?: PaymentTargetType) {
        if (!isDefined(entity_id) || !isDefined(entity_type)) {
            getMainPage().errorToastElement?.showToast('No entity found');
            return;
        }
        this.reloading = true;

        this.payment_target_entity_id = entity_id;

        switch (entity_type) {
            case PaymentTargetType.Form:
                this.product = this.defaultFormProductRequest;
                break;
            case PaymentTargetType.Paywall:
                this.product = this.defaultPaywallProductRequest;
                break;
            case PaymentTargetType.Ballot:
                this.product = this.defaultBallotProductRequest;
                break;
            case PaymentTargetType.ActivatedChallenge:
                this.product = this.defaultActivatedChallengeProductRequest;
                break;
            default:
                this.product = this.defaultFormProductRequest;
        }

        if (isDefined(this.product) && 'Form' in this.product.item_details) {
            this.getVisibilityOptions();
        }

        if (isDefined(this.product) && 'ActivatedChallenge' in this.product.item_details) {
            this.product.item_details.ActivatedChallenge.challenge_id.$oid = entity_id;
        }

        this.reloading = false;
    }

    async createProduct() {
        if (!isDefined(this.payment_target_entity_id) || !isDefined(this.payment_target_type) || !isDefined(this.product)) {
            getMainPage().errorToastElement?.showToast('No entity found');
            return;
        }
        this.reloading = true;

        // send call to create product
        await createOrganizationProduct(organizationInfo.organization?._id?.$oid ?? '', this.product);

        this.dispatchEvent(new CustomEvent('product-updated'));
        this.product = undefined;
        this.existing_product = undefined;
        this.previewMode = false;

        getMainPage().errorToastElement?.showToast('Product created successfully');

        this.reloading = false;
    }

    async updateProduct() {
        if (!this.payment_target_entity_id || !isDefined(this.payment_target_type) || !isDefined(this.existing_product) || !isDefined(this.product)) {
            getMainPage().errorToastElement?.showToast('No entity found');
            return;
        }

        await updateOrganizationProduct(organizationInfo.organization?._id?.$oid ?? '', this.product);

        getMainPage().errorToastElement?.showToast('Product updated successfully');

        // dispatch a message sent event
        this.dispatchEvent(new CustomEvent('product-updated'));

        this.product = undefined;
        this.existing_product = undefined;
        this.previewMode = false;
    }

    private async exitPreview() {
        this.previewMode = false;
        await delay(200); // Wait for the DOM to update
    }

    private async close() {
        this.previewMode = false;
        this.product = undefined;
        this.existing_product = undefined;
        this.dispatchEvent(new CustomEvent('close'));
    }

    renderReloading() {
        return html`
            <loading-element
            .loadingString=${'Loading Product...'}
            .horizontal=${true}></loading-element>`;
    }

    renderSending() {
        return html`
            <loading-element
            .loadingString=${'Sending Message. This may take a few minutes, depending on the number of participants.'}
            .horizontal=${true}></loading-element>`;
    }

    renderCreate() {
        if (!this.payment_target_entity_id || !isDefined(this.payment_target_type)) {
            return html`
                None
            `;
        }
        return html`
            <div class="modal__header" @click="${async () => await this.instantiateProduct(this.payment_target_entity_id, this.payment_target_type)}">
                <form-button variant="outline" size="sm" ?fullWidth=${true}>${this.actionButtonText}</form-button>
            </div>
            `
        ;
    }

    renderPreview() {
        // For Email, use existing preview
        return html`
        <div class="preview-container">
                <div class="preview-header" style="display: flex; justify-content: space-between; align-items: center;">
                    <div class="preview-subject">
                        <h3>Preview</h3>
                    </div>
                    <form-button
                        variant="outline"
                        size="sm"
                        class="close-button"
                        @click="${this.exitPreview}">
                        Close Preview
                    </form-button>
                </div>
                <div class="sms-preview-content" style="background: #f5f5f5; padding: 16px; border-radius: 8px; margin-top: 16px; font-family: monospace; border: 1px solid #ddd;">
                    <pre-checkout-component
                        .pendingProduct=${this.product}
                        .isPreview=${true}
                        .organization_id=${topicData.topic?.organization_id?.$oid ?? ''}
                    >
                    </pre-checkout-component>
                </div>
                <br />
                Users will be asked to pay upon completion of the form.
            </div>
            `;
    }

    getPriceTypeFromProduct(): string {
        if (!this.product?.pricing_structure) {
            return 'Fixed';
        }

        // Check if it's a Fixed price structure
        if ('Fixed' in this.product.pricing_structure) {
            return 'fixed';
        }

        // Check if it's a Recurring price structure
        if ('Recurring' in this.product.pricing_structure) {
            return 'recurring';
        }

        // Check if it's a Donation price structure
        if ('Donation' in this.product.pricing_structure) {
            return 'donation';
        }

        return 'Fixed';
    }

    setProductPriceType(priceType: 'fixed' | 'recurring' | 'donation') {
        if (!isDefined(this.product)) return;

        // Don't override if the product already has the correct price type
        if ((priceType === 'fixed' && 'Fixed' in this.product.pricing_structure) ||
            (priceType === 'recurring' && 'Recurring' in this.product.pricing_structure) ||
            (priceType === 'donation' && 'Donation' in this.product.pricing_structure)) {
            return;
        }

        const pricingStructures: Record<'fixed' | 'recurring' | 'donation', PriceStructure> = {
            fixed: {
                Fixed: { amount_cents: 500 }
            },
            recurring: {
                Recurring: {
                    amount_cents: 500,
                    interval: ProductPeriod.Monthly // <-- use the enum variant that matches your model
                }
            },
            donation: {
                Donation: {
                    default_amount_cents: 500,
                    suggested_amounts_cents: [500, 1000, 5000],
                    min_amount_cents: 100,
                    max_amount_cents: 100000,
                    allow_custom_amount: true,
                    required: false
                }
            }
        };

        this.product.pricing_structure = pricingStructures[priceType];
        this.requestUpdate();
    }

    render() {
        if (this.stripeInfo?.charges_enabled === false) {
            return html`
                <div class="modal__header">
                    <h4 class="sub-title">Payment Account Not Fully Set Up</h4>
                    <p>Your payment account is not fully set up to accept payments. Please complete the setup in your organization settings.</p>
                </div>
            `;
        }

        if (this.reloading) {
            return this.renderReloading();
        }

        if (this.payment_target_entity_id && isDefined(this.payment_target_type) && !isDefined(this.existing_product) && !isDefined(this.product)) {
            return this.renderCreate();
        }

        if (this.previewMode && isDefined(this.product)) {
            return this.renderPreview();
        }

        return html`
                <div class="wrapper">
                    <h4 class="sub-title">Product Details</h4>
                        <proxi-textfield ?disabled=""
                            outlined
                            class="input__challenge-title"
                            id="productName"
                            type="text"
                            label="Product Name"
                            placeholder="Enter product name"
                            @change="${(e: Event) => {
                                const input = e.target as ProxiTextfield;
                                if (!isDefined(this.product)) return;
                                this.product.name = input.value;
                            }}"
                            value="${isDefined(this.existing_product) ? this.existing_product.name : isDefined(this.product) ? this.product.name : ''}"
                        >
                        </proxi-textfield>
                        <proxi-textfield ?disabled=""
                            outlined
                            class="input__challenge-title"
                            id="productDescr"
                            type="text"
                            label="Product Description"
                            placeholder="Enter product description"
                            @change="${(e: Event) => {
                                const input = e.target as ProxiTextfield;
                                if (!isDefined(this.product)) return;
                                this.product.description = input.value;
                            }}"
                            value="${isDefined(this.existing_product) ? this.existing_product.description : isDefined(this.product) ? this.product.description : ''}"
                        >
                        </proxi-textfield>
                        <proxi-textfield ?disabled=""
                            outlined
                            class="input__challenge-title"
                            id="statementDescriptor"
                            type="text"
                            character-limit="22"
                            label="Statement Descriptor"
                            placeholder="Enter statement descriptor"
                            @change="${(e: Event) => {
                                const input = e.target as ProxiTextfield;
                                if (!isDefined(this.product)) return;
                                this.product.statement_descriptor = input.value;
                            }}"
                            value="${isDefined(this.existing_product) ? this.existing_product.statement_descriptor : isDefined(this.product) ? this.product.statement_descriptor : ''}"
                        >
                        </proxi-textfield>
                        <proxi-textfield ?disabled=""
                            outlined
                            class="input__challenge-title"
                            id="supportEmail"
                            type="email"
                            label="Support Email"
                            placeholder="Enter support email"
                            @change="${(e: Event) => {
                                const input = e.target as ProxiTextfield;
                                if (!isDefined(this.product)) return;
                                this.product.support_email = input.value;
                            }}"
                            value="${isDefined(this.existing_product) ? this.existing_product.support_email : isDefined(this.product) ? this.product.support_email : ''}"
                        >
                        </proxi-textfield>
                        <proxi-selectfield
                            id="currencyCode"
                            ?disabled="${isDefined(this.existing_product)}"
                            outlined
                            label="Currency"
                            value="${isDefined(this.existing_product) ? this.existing_product.currency_code.toLowerCase() : isDefined(this.product) ? this.product.currency_code.toLowerCase() : 'usd'}"
                            @selected="${(e: CustomEvent) => {
                                if (!isDefined(this.product)) return;
                                this.product.currency_code = (e.target as ProxiSelectfield).value.toLowerCase();
                                this.requestUpdate();
                            }}"
                        >
                            ${ALLOWED_CURRENCIES.map(currency => html`
                                <mwc-list-item value="${currency.code}">${currency.name}</mwc-list-item>
                            `)}
                        </proxi-selectfield>
                    <h4 class="sub-title">Price Type</h4>
                        <proxi-selectfield
                            id="priceType"
                            ?disabled="${isDefined(this.existing_product)}"
                            outlined
                            value="${this.getPriceTypeFromProduct()}"
                            @selected="${(e: CustomEvent) => this.setProductPriceType((e.target as ProxiSelectfield).value as 'fixed' | 'recurring' | 'donation')}"
                            >
                            <mwc-list-item value="fixed">One-Time Payment</mwc-list-item>
                            <mwc-list-item value="recurring">Subscription (Recurring Charge)</mwc-list-item>
                            <mwc-list-item value="donation">Donation</mwc-list-item>
                        </proxi-selectfield>
                        ${this.renderAdditionalPaymentOptions()}
                        ${this.renderAdditionalTypeOptions()}
                        <div class="button__wrapper">
                            <div style="display: flex; align-items: center; gap: 10px; justify-content: flex-start;flex-direction: row;">
                                <form-button
                                    class="challeng-save-button"
                                    @click="${this.close}"
                                    size="sm"
                                >
                                    Close
                                </form-button>
                                ${isDefined(this.product) ? html`
                                    <form-button
                                        class="challeng-save-button"
                                        @click="${async () => await this.openPreview(this.product)}"
                                        size="sm"
                                        variant="outline"
                                    >
                                        Preview Now
                                    </form-button>
                                ` : ''}
                            </div>
                            <form-button
                                style=""
                                class="challeng-save-button"
                                @click="${isDefined(this.existing_product) ? this.updateProduct : this.createProduct}"
                                size="sm"
                            >
                                ${this.actionButtonText}
                            </form-button>
                        </div>
                </div>
            </div>
        `;
    }

    get actionButtonText(): string {
        if (this.payment_target_type === PaymentTargetType.Paywall) {
            return isDefined(this.existing_product) ? 'Update Paywall' : 'Create Paywall';
        }
        return isDefined(this.existing_product) ? 'Update Product' : 'Create Product';
    }

    renderAdditionalPaymentOptions() {
        if (!isDefined(this.product)) {
            return '';
        }
        if ('Fixed' in this.product.pricing_structure) {
            return this.renderFixedPriceOptions();
        }
        if ('Recurring' in this.product.pricing_structure) {
            return this.renderSubscriptionOptions();
        }
        if ('Donation' in this.product.pricing_structure) {
            return this.renderDonationOptions();
        }
        return '';
    }

    renderFixedPriceOptions() {
        return html`
            <h4 class="sub-title">Fixed Price Options</h4>
            <proxi-textfield ?disabled=""
                outlined
                class="input__challenge-title"
                id="fixedAmount"
                type="number"
                label="Cost"
                placeholder="Enter fixed amount in cents"
                suffix="${this.product ? this.product.currency_code.toUpperCase() : 'USD'}"
                value="${this.product ? getPriceCentsFromProduct(this.product) / 100 : 1}"
                @change="${(e: Event) => {
                    const input = e.target as ProxiTextfield;
                    if (!isDefined(this.product) || !('Fixed' in this.product.pricing_structure)) return;
                    (this.product.pricing_structure as any).Fixed.amount_cents = Math.round(parseFloat(input.value) * 100);
                }}"
            >
            </proxi-textfield>
        `;
    }

    renderSubscriptionOptions() {
        return html`
            <h4 class="sub-title">Subscription Options</h4>
            ${this.existing_product !== undefined ? html`<p class="info-paragraph">Note: Changing subscription pricing will not affect existing subscribers.</p>` : ''}
            <proxi-textfield ?disabled=""
                outlined
                class="input__challenge-title"
                id="subscriptionAmount"
                type="number"
                label="Cost"
                placeholder="Enter subscription amount in cents"
                value="${this.getSubscriptionAmount()/100}"
                @change="${(e: Event) => {
                    const input = e.target as ProxiTextfield;
                    if (!isDefined(this.product) || !('Recurring' in this.product.pricing_structure)) return;
                    const parsedAmount = parseFloat(input.value ?? '0');
                    (this.product.pricing_structure as any).Recurring.amount_cents = Math.round(isNaN(parsedAmount) ? 0 : parsedAmount * 100);
                }}"
            >
            </proxi-textfield>
            <proxi-selectfield ?disabled=""
                id="subscriptionInterval"
                outlined
                value="${this.getSubscriptionInterval()}"
                 @selected="${(e: CustomEvent) => {
                     if (!isDefined(this.product) || !('Recurring' in this.product.pricing_structure)) return;
                     const select = e.target as ProxiSelectfield;
                     const value = select.value as 'week' | 'month' | 'year';
                     const interval = value === 'week' ? ProductPeriod.Weekly : value === 'year' ? ProductPeriod.Yearly : ProductPeriod.Monthly;
                     (this.product.pricing_structure as any).Recurring.interval = interval;
                 }}"
                >
                <mwc-list-item value="week">Weekly</mwc-list-item>
                <mwc-list-item value="month">Monthly</mwc-list-item>
                <mwc-list-item value="year">Yearly</mwc-list-item>
            </proxi-selectfield>
        `;
    }

    renderDonationOptions() {
        return html`
            <h4 class="sub-title">Donation Options</h4>
            <p class="info-paragraph">Set minimum/maximum donation limits.</p>
            <proxi-textfield
                outlined
                class="donation-min-amount"
                id="donationMinAmount"
                type="number"
                label="Minimum Amount"
                suffix="${this.product ? this.product.currency_code.toUpperCase() : 'USD'}"
                placeholder="Enter minimum donation amount in cents"
                @change="${(e: Event) => {
                    const input = e.target as ProxiTextfield;
                    if (!isDefined(this.product) || !('Donation' in this.product.pricing_structure)) return;
                    (this.product.pricing_structure as any).Donation.min_amount_cents = Math.round(parseFloat(input.value) * 100);
                }}"
                value="${this.product && 'Donation' in this.product.pricing_structure ? (this.product.pricing_structure as any).Donation.min_amount_cents / 100 : 1}">
            </proxi-textfield>
            <proxi-textfield
                outlined
                class="donation-min-amount"
                id="donationMaxAmount"
                type="number"
                label="Maximum Amount"
                suffix="${this.product ? this.product.currency_code.toUpperCase() : 'USD'}"
                placeholder="Enter maximum donation amount"
                @change="${(e: Event) => {
                    const input = e.target as ProxiTextfield;
                    if (!isDefined(this.product) || !('Donation' in this.product.pricing_structure)) return;
                    (this.product.pricing_structure as any).Donation.max_amount_cents = Math.round(parseFloat(input.value) * 100);
                }}"
                value="${this.product && 'Donation' in this.product.pricing_structure ? (this.product.pricing_structure as any).Donation.max_amount_cents / 100 : 1000}"
            >
            </proxi-textfield>
            <p class="info-paragraph">Set suggested donation amounts.</p>
            <proxi-textfield
                outlined
                class="donation-suggested-amount"
                id="donationSuggestedAmount"
                type="number"
                label="Suggested Amount 1"
                suffix="${this.product ? this.product.currency_code.toUpperCase() : 'USD'}"
                placeholder="Enter suggested donation amount"
                value="${this.product && 'Donation' in this.product.pricing_structure ? ((this.product.pricing_structure as any).Donation.suggested_amounts_cents[0] / 100) : 1}"
                @change="${(e: Event) => {
                    const input = e.target as ProxiTextfield;
                    if (!isDefined(this.product) || !('Donation' in this.product.pricing_structure)) return;
                    (this.product.pricing_structure as any).Donation.suggested_amounts_cents[0] = Math.round(parseFloat(input.value) * 100);
                }}"
            >
            </proxi-textfield>
            <proxi-textfield
                outlined
                class="donation-suggested-amount"
                id="donationSuggestedAmount2"
                type="number"
                label="Suggested Amount 2"
                suffix="${this.product ? this.product.currency_code.toUpperCase() : 'USD'}"
                placeholder="Enter suggested donation amount"
                value="${this.product && 'Donation' in this.product.pricing_structure ? ((this.product.pricing_structure as any).Donation.suggested_amounts_cents[1] / 100) : 5}"
                @change="${(e: Event) => {
                    const input = e.target as ProxiTextfield;
                    if (!isDefined(this.product) || !('Donation' in this.product.pricing_structure)) return;
                    (this.product.pricing_structure as any).Donation.suggested_amounts_cents[1] = Math.round(parseFloat(input.value) * 100);
                }}"
            >
            </proxi-textfield>
            <proxi-textfield
                outlined
                class="donation-suggested-amount"
                id="donationSuggestedAmount3"
                type="number"
                label="Suggested Amount 3"
                suffix="${this.product ? this.product.currency_code.toUpperCase() : 'USD'}"
                placeholder="Enter suggested donation amount"
                value="${this.product && 'Donation' in this.product.pricing_structure ? ((this.product.pricing_structure as any).Donation.suggested_amounts_cents[2] / 100) : 10}"
                @change="${(e: Event) => {
                    const input = e.target as ProxiTextfield;
                    if (!isDefined(this.product) || !('Donation' in this.product.pricing_structure)) return;
                    (this.product.pricing_structure as any).Donation.suggested_amounts_cents[2] = Math.round(parseFloat(input.value) * 100);
                }}"
            >
            </proxi-textfield>
            <p class="info-paragraph">Optional Amount</p>
            <div class="checkbox-wrapper">
                <input
                    type="checkbox"
                    id="allowCustomAmount"
                    name="allowCustomAmount"
                    ?checked="${this.product && 'Donation' in this.product.pricing_structure ? (this.product.pricing_structure).Donation.allow_custom_amount : this.existing_product && 'Donation' in this.existing_product.pricing_structure ? this.existing_product.pricing_structure.Donation.allow_custom_amount : true}"
                    @change="${(e: Event) => {
                        const input = e.target as HTMLInputElement;
                        if (!isDefined(this.product) || !('Donation' in this.product.pricing_structure)) return;
                        (this.product.pricing_structure as any).Donation.allow_custom_amount = input.checked;
                    }}"
                />
                <label for="allowCustomAmount">Allow donors to enter a custom donation amount</label>
            </div>
            <p class="info-paragraph">Require Donation</p>
            <div class="checkbox-wrapper">
                <input
                    type="checkbox"
                    id="requireDonationAmount"
                    name="requireDonationAmount"
                    ?checked="${this.product && 'Donation' in this.product.pricing_structure ? (this.product.pricing_structure).Donation.required : this.existing_product && 'Donation' in this.existing_product.pricing_structure ? this.existing_product.pricing_structure.Donation.required : false}"
                    @change="${(e: Event) => {
                        const input = e.target as HTMLInputElement;
                        if (!isDefined(this.product) || !('Donation' in this.product.pricing_structure)) return;
                        (this.product.pricing_structure).Donation.required = input.checked;
                    }}"
                />
                <label for="requireDonationAmount">Require a donation of at least the minimum amount to proceed</label>
            </div>
        `;
    }

    private getSubscriptionAmount(): number {
        if (!this.product?.pricing_structure) {
            return 100;
        }

        if ('Recurring' in this.product.pricing_structure) {
            const subscription = (this.product.pricing_structure as any).Recurring;
            return subscription.amount_cents;
        }

        return 100;
    }

    private getSubscriptionInterval(): string {
        if (!this.product?.pricing_structure) {
            return 'month';
        }

        if ('Recurring' in this.product.pricing_structure) {
            const subscription = (this.product.pricing_structure as any).Recurring;
            return subscription.interval === ProductPeriod.Weekly ? 'week' : subscription.interval === ProductPeriod.Yearly ? 'year' : 'month';
        }

        return 'month';
    }

    renderAdditionalTypeOptions() {
        if (!isDefined(this.product)) {
            return '';
        }
        if ('Form' in this.product.item_details) {
            return this.renderFormOptions();
        }
        return '';
    }

    private async updateProductVisibilityRules(fieldName: string, visibilityRules: any) {
        if (!isDefined(this.product)) {
            return;
        }
        if ('Form' in this.product.item_details) {
            this.product.item_details.Form.visibility_rules = visibilityRules;
        }
        this.requestUpdate();

    }

    private async addNewVisibilityRule(customization: string): Promise<void> {
        const visibilityComponent = this.shadowRoot?.querySelector(`#formVisibilityComponent_${customization}`) as FormVisibilityComponent;

        if (isDefined(visibilityComponent) && typeof visibilityComponent.addAndRule === 'function') {
            await visibilityComponent.addAndRule();
            this.requestUpdate();
        }
    }

    renderFormOptions() {
        if (!isDefined(topicData.topic?.add_point_customization)) {
            return html``;
        }
        const visibilityRules = this.product && ('Form' in this.product?.item_details) ? this.product?.item_details.Form.visibility_rules ?? [] : [];
        return html`
            <h4 class="sub-title">Form Options</h4>
                <div class="visibilityOptions">
                    <p class="helpSubeHeaderUnderline">Charge Logic (optional)</p>
                        <p class="info-paragraph">${visibilityRules.length > 0 ? 'The field will only appear on the form when:' : 'This product will always appear on the form.'}</p>
                            <form-visibility-component
                                id="formVisibilityComponent_${this.product?._id?.$oid ?? 'new'}"
                                style="${isDefined(visibilityRules) && visibilityRules.length > 0 ? '' : 'hidden'}"
                                .visibilityRules=${visibilityRules}
                                .visibilityTargetOptions=${this.visibilityTargetOptions}
                                .customFormElements=${topicData.topic?.add_point_customization?.custom_fields ?? []}
                                .currentFieldValue=${this.product?._id?.$oid ?? 'new'}
                                @visibility-rules-updated=${async (e: CustomEvent) => {
                                    await this.updateProductVisibilityRules(e.detail.fieldName, e.detail.visibilityRules);
                                    this.requestUpdate();
                                }}
                            ></form-visibility-component>
                    ${!isDefined(visibilityRules) || visibilityRules?.length === 0 ? html`
                        <br>
                        <button class="add_visibility_rule" @click=${async () => await this.addNewVisibilityRule(this.product?._id?.$oid ?? 'new')}>
                            Add Logic Rule
                        </button>
                    ` : html`
                        <div class="add-new-rule-wrapper">
                            <div class="operator-holder" @click=${async () => await this.addNewVisibilityRule(this.product?._id?.$oid ?? 'new')}>
                                <span class="operator and clickable-and">
                                    <mwc-icon>add_circle</mwc-icon> AND
                                </span>
                            </div>
                        </div>
                    `}
                </div>
        `;
    }

    renderPaywallOptions() {
        return html`
            <h4 class="sub-title">Paywall Options</h4>
            <proxi-textfield ?disabled=""
                outlined
                class="input__challenge-title"
                id="gateId"
                type="text"
                label="Gate ID"
                placeholder="Enter gate ID"
            >
            </proxi-textfield>
        `;
    }

    private getVisibilityOptions() {
       this.visibilityTargetOptions = standardFields;
       // find the one with the name category and add the optiosn there
        const categoryField = this.visibilityTargetOptions.find(field => field.name === 'Category');
        if (categoryField) {
            categoryField.options = topicData.categories.map(category => {
                return {
                    value: category._id?.$oid ?? '',
                    name: category.name
                };
            }
        );
        }
        // add tag options
        const tagField = this.visibilityTargetOptions.find(field => field.name === 'Tags');
        if (tagField && topicData.allUserTags.length > 0) {
            tagField.options = topicData.allUserTags.map(tag => {
                return {
                    value: tag._id?.$oid ?? '',
                    name: tag.name
                };
            }
        );
        }

        const customVisibilityOptions = (topicData.topic?.add_point_customization?.custom_fields ?? []).map(field => {
            let type: string;
            if ('MultiSelect' in field.field_type || 'SingleSelect' in field.field_type) {
                type = 'select';
            } else if ('Number' in field.field_type) {
                type = 'number';
            } else {
                type = 'text';
            }
            return {
                value: field._id?.$oid ?? '',
                name: field.customizations.header ?? 'Custom Field',
                formPart: LogicFieldType.CustomField,
                inputType: type as 'text' | 'select' | 'number',
                options: 'MultiSelect' in field.field_type && isDefined(field.field_type.MultiSelect)  ? field.field_type.MultiSelect.options.map(option => {
                    return {
                        value: option,
                        name: option
                    };
                }
                ) : 'SingleSelect' in field.field_type && isDefined(field.field_type.SingleSelect) ? field.field_type.SingleSelect.options.map(option => {
                    return {
                        value: option,
                        name: option
                    };
                }
                ) : []
                };
            }
        );

        // Remove existing options that match the new ones
        this.visibilityTargetOptions = this.visibilityTargetOptions.filter(targetOption =>
            !customVisibilityOptions.some(customOption => customOption.value === targetOption.value)
        );

        // Add the new options
        this.visibilityTargetOptions.push(...customVisibilityOptions);
    }



    static get styles() {
        return css`
            :host {
                --modal-width: 800px;
                font-size: 12px;
                margin-top: 10px;
            }

            .wrapper {
                display: flex;
                flex-direction: column;
                gap: 10px;
                margin-top: 10px;
            }

            .modalContent {
                position: relative;
                width: 100%;
                max-height: calc(80vh - 180px);
                height: auto;
                overflow-y: auto;
                box-sizing: border-box;
            }

            .modal__header {
                width: 100%;
                text-align: center;
                padding: 0px 20px 5px;
                color: #000000;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                box-sizing: border-box;
            }

            .modal__content {
                padding: 20px 0 5px;
                width: 100%;
                box-sizing: border-box;
            }

            .header__actions {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
                box-sizing: border-box;
            }


            .add-new-rule-wrapper {
                width: 100%;
                display: flex;
                justify-content: center;
                padding-top: 5px;
            }

            .header-row {
                cursor: pointer;
                width: 100%;
                align-items: center;
                font-weight: 700;
                display: flex;
                height: 40px;
                background-color: var(--theme-border-color);
                gap: 10px;
            }

            .header-row mwc-icon {
                font-size: 20px;
            }

            .header-cell,
            .data-cell {
                align-items: center;
            }

            .header-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .header-cell.text {
                text-align: left;
                width: 35%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .header-cell.number {
                text-align: center;
                width: 15%;
                justify-content: center;
            }

            .header-cell.primary {
                flex: 1;
            }

            .data-cell {
                display: flex;
                align-items: center;
                padding: 0 10px;
            }

            .data-cell.text {
                text-align: left;
                width: 35%;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .data-cell.number {
                text-align: center;
                width: 15%;
                justify-content: center;
            }

            .data-cell.primary {
                flex: 1;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                --mdc-typography-button-text-transform: capitalize;
            }

            .aqua-blue {
                --mdc-theme-primary: var(--main-secondary-color);
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
            }

                        .existingRules {
                display: flex;
                flex-direction: column;
                --mdc-icon-size: 16px;
            }

            .existingRules mwc-icon {
                cursor: pointer;
                margin-right: 0.5rem;
                margin-left: 0.5rem;
            }

            .ruleName {
                margin: 0;
                font-size: 0.8rem;
                text-align: center;
                flex: 1;
                border: 1px solid var(--main-secondary-color);
                display: flex;
                align-items: center;
                gap: 5px;
            }

            .rule {
                display: flex;
                flex-direction: column;
                align-items: center;
                border: 1px solid var(--main-primary-color);
                padding: 0.5rem;
            }

            .operator-holder {
                display: flex;
                justify-content: center;
                flex-direction:row;

            }

            .operator {
                text-align: center;
                font-size: 0.6rem;
                font-weight: bold;
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;
                color: white;
                border-radius: 5px;
                padding: 0 0.5rem;
                --mdc-icon-size: 12px;
            }

            .operator mwc-icon {
                margin-left: 0;
                margin-right: 0.25rem;

            }

            .and {
                background-color: var(--main-primary-color);
                color : white;
            }

            .or {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-or {
                cursor: pointer;
                color: var(--main-secondary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-or:hover {
                background-color: var(--main-secondary-color);
                color: white;
            }

            .clickable-and {
                cursor: pointer;
                color: var(--main-primary-color);
                background-color: white;
                display: inline-flex;
                align-items: center;
            }

            .clickable-and:hover {
                background-color: var(--main-primary-color);
                color: white;
            }

            .visibilityRules {
                display: flex;
                flex-direction: column;
                margin-top: 1rem;
            }

            .helpSubeHeaderUnderline {
                margin: 0px;
                font-weight: normal;
                font-size: 0.8rem;
                flex: 1;
                text-decoration: underline;
                color: var(--theme-primary-color);
                display: inline-flex;
            }

            .challenges-wrapper {
                width: 100%;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                box-sizing: border-box;
            }

            .challenge-row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 5px;
                box-sizing: border-box;
                border-bottom: 1px solid #DADADA;
                gap: 10px;
            }

            .challenge-row:hover {
                background-color: var(--theme-border-color);
                cursor: context-menu;
            }

            .sub-title {
                margin-bottom: 5px;
                color: var(--theme-input-color);
                display: inline-flex;
                --mdc-icon-size: 16px;
            }

            .user {
                color: #102C3D;
                text-align: left;
                line-height: 125%;
            }

            .stats {
                box-sizing: border-box;
                color: #102C3D;
                text-align: center;
                line-height: 125%;
            }

            .view-more {
                cursor: pointer;
                color: #102C3D;
                text-align: right;
                font-weight: 700;
                line-height: 125%;
                text-decoration-line: underline;
            }

            .button__wrapper {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                margin: 15px 0;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}
